import { Icon } from '@cmbg-traky-web/design-system';
import { useCallback, useState } from 'react';

import { TableLoadingConfig } from '@/core/types/table-loading-config';

import { useGlobalErrorHandler } from './useGlobalErrorHandler';

interface UseLoadingState {
  loading: boolean;
  error: boolean | Error | null;
}

/**
 * Hook para manejar estados de carga y errores. Proporciona funcionalidad para controlar loading states y configuraciones de tabla.
 *
 * @returns {Object} Objeto con el estado de carga, funciones para actualizar el estado y configuración de loading para tablas.
 */
export const useLoading = () => {
  const [state, setState] = useState<UseLoadingState>({
    loading: false,
    error: null
  });

  const { handleError } = useGlobalErrorHandler({ isCritical: false });

  const setLoadingState = useCallback(
    (newState: UseLoadingState) => {
      setState(newState);

      if (
        newState.error &&
        newState.error instanceof Error &&
        newState.error.constructor.name !== 'WebOrderError'
      ) {
        handleError(newState.error, 'Loading hook error');
      }
    },
    [handleError]
  );

  const getTableLoadingConfig = (): boolean | TableLoadingConfig => {
    if (state.loading) {
      return {
        size: 'large' as const,
        delay: 300,
        indicator: <Icon loading={state.loading} />
      };
    }

    return state.loading;
  };

  return { ...state, setLoadingState, getTableLoadingConfig };
};
